import { useEffect, useState } from 'react';
import { isNativeApp, whenShellKnown } from '../../lib/nativeApp';

/**
 * The Android app as the server offers it for download (`/android/`, see
 * DEPLOYMENT.md): an installed app older than the one on the server is told
 * about the newer one. The app cannot update itself — Android installs an APK
 * only through its own installer — so it ends in a download. Browsers are not
 * offered the app; it is handed out to the people who use it.
 */

export interface PublishedApp {
    version: string;
    downloadUrl: string;
}

const VERSION_URL = '/android/app-version.json';

/**
 * What the server offers for `appId`; null when nothing is published, or
 * offline. One file for both apps, keyed by package — dev and live deploy the
 * same repo, and the dev app must not be handed the live one:
 * `{ "de.ast.data": { "version": "1.6.0", "file": "ast-data-live-1.6.0.apk" } }`.
 */
export async function fetchPublishedApp(
    appId: string
): Promise<PublishedApp | null> {
    try {
        const response = await fetch(VERSION_URL, { cache: 'no-store' });
        if (!response.ok) {
            return null;
        }
        const body: unknown = await response.json();
        if (typeof body !== 'object' || body === null) {
            return null;
        }
        const entry = (body as Record<string, unknown>)[appId];
        if (typeof entry !== 'object' || entry === null) {
            return null;
        }
        const { version, file } = entry as Record<string, unknown>;
        if (
            typeof version !== 'string' ||
            typeof file !== 'string' ||
            !/^[\w.-]+\.apk$/.test(file)
        ) {
            return null;
        }
        return { version, downloadUrl: `/android/${file}` };
    } catch {
        return null;
    }
}

function versionParts(version: string): number[] {
    // "1.5.0-dev": the dev app's suffix is not part of the comparison.
    return version
        .split(/[.-]/)
        .slice(0, 3)
        .map((part) => Number.parseInt(part, 10) || 0);
}

/** True when `candidate` ("1.6.0") is newer than `installed` ("1.5.0-dev"). */
export function isNewerVersion(candidate: string, installed: string): boolean {
    const candidateParts = versionParts(candidate);
    const installedParts = versionParts(installed);
    for (let i = 0; i < 3; i++) {
        const difference = (candidateParts[i] ?? 0) - (installedParts[i] ?? 0);
        if (difference !== 0) {
            return difference > 0;
        }
    }
    return false;
}

/** Asked once per page load; a newer APK shows after the next reload. */
let published: Promise<PublishedApp | null> | null = null;

/**
 * Inside the app: the newer app waiting on the server, null while the app is
 * up to date, nothing is published, or offline. Always null in a browser.
 */
export function useAndroidAppUpdate(): PublishedApp | null {
    const [update, setUpdate] = useState<PublishedApp | null>(null);

    useEffect(() => {
        if (!isNativeApp()) {
            return;
        }
        let cancelled = false;
        void whenShellKnown().then(async (shell) => {
            if (!shell) {
                return;
            }
            published ??= fetchPublishedApp(shell.appId);
            const app = await published;
            if (
                !cancelled &&
                app &&
                isNewerVersion(app.version, shell.version)
            ) {
                setUpdate(app);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return update;
}
